import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { BackendUserRepo } from '@monsido/modules/endpoints/api/backend_admin/backend-user-repo';
import { LoginUserRepo } from '@monsido/modules/endpoints/api/login/login-user.repo';
import { UserInterface } from '@monsido/modules/models/api/interfaces/user.interface';
import { environment } from '../../../../environments/environment';

export interface EmailSearchResult {
    email: string;
    environments: EnvironmentResult[];
    totalUsers: number;
}

export interface EnvironmentResult {
    name: string;
    url: string;
    users: UserInterface[];
    searched: boolean;
    error?: string;
}

@Injectable({
    providedIn: 'root',
})
export class BackendAdminDeleteUsersService {

    constructor (
        private userRepo: BackendUserRepo,
        private loginUserRepo: LoginUserRepo,
    ) {}

    /**
     * Extract email addresses from any text using regex
     */
    extractEmailsFromText (text: string): string[] {
        const emails: string[] = [];

        // Simple email regex to find any email addresses in the text
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const matches = text.match(emailRegex);

        if (matches) {
            for (const email of matches) {
                const cleanEmail = email.trim();
                if (cleanEmail && !emails.includes(cleanEmail)) {
                    emails.push(cleanEmail);
                }
            }
        }

        return emails;
    }

    /**
     * Search for users across all environments for a list of emails
     */
    async searchUsersAcrossEnvironments (
        emails: string[],
        progressCallback?: (progress: number, total: number) => void,
    ): Promise<EmailSearchResult[]> {
        const searchResults: EmailSearchResult[] = [];
        let searchProgress = 0;
        const totalSearches = emails.length * environment.apiEnvs.length;

        // Search each email across all environments
        for (const email of emails) {
            const emailResult: EmailSearchResult = {
                email: email,
                environments: [],
                totalUsers: 0,
            };

            // Search across all environments for this email
            for (const env of environment.apiEnvs) {
                const envResult: EnvironmentResult = {
                    name: env.name,
                    url: env.url,
                    users: [],
                    searched: false,
                };

                try {
                    const users = await this.searchUserInEnvironment(email, env.url);
                    envResult.users = users || [];
                    envResult.searched = true;
                    emailResult.totalUsers += envResult.users.length;
                } catch (error) {
                    envResult.error = `Search failed: ${error}`;
                    envResult.searched = true;
                }

                emailResult.environments.push(envResult);
                searchProgress++;

                // Call progress callback if provided
                if (progressCallback) {
                    progressCallback(searchProgress, totalSearches);
                }
            }

            searchResults.push(emailResult);
        }

        return searchResults;
    }

    /**
     * Search for a user in a specific environment
     */
    private searchUserInEnvironment (email: string, apiUrl: string): Promise<UserInterface[]> {
        return this.userRepo.getAll(
            { search: email },
            new HttpHeaders({ noGlobal: 'true', noParseSearch: 'true' }),
            apiUrl + '/admin/api/',
        );
    }

    /**
     * Delete users from all environments using bulk delete API
     */
    async deleteUsersFromAllEnvironments (searchResults: EmailSearchResult[]): Promise<void> {
        // Collect all unique emails that have users found
        const emailsToDelete: string[] = [];

        for (const emailResult of searchResults) {
            // Only include emails that have users found in at least one environment
            if (emailResult.totalUsers > 0) {
                emailsToDelete.push(emailResult.email);
            }
        }

        if (emailsToDelete.length === 0) {
            return; // Nothing to delete
        }

        // Use the login API to bulk delete users across all environments
        // The all_envs: true parameter tells the API to delete from all environments
        await this.loginUserRepo.bulkDelete(
            emailsToDelete,
            true, // all_envs: true
            environment.monsidoPath, // Explicitly set the login API URL
        );
    }

    /**
     * Get display name for a user
     */
    getUserDisplayName (user: UserInterface): string {
        if (user.first_name && user.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        if (user.first_name) {
            return user.first_name;
        }
        if (user.last_name) {
            return user.last_name;
        }
        return 'N/A';
    }

    /**
     * Filter emails that have users found
     */
    filterEmailsWithUsers (searchResults: EmailSearchResult[]): EmailSearchResult[] {
        return searchResults.filter(emailResult => emailResult.totalUsers > 0);
    }

    /**
     * Filter emails that have no users found
     */
    filterEmailsWithoutUsers (searchResults: EmailSearchResult[]): string[] {
        return searchResults
            .filter(emailResult => emailResult.totalUsers === 0)
            .map(emailResult => emailResult.email);
    }

    /**
     * Filter environments that have users for a specific email
     */
    filterEnvironmentsWithUsers (emailResult: EmailSearchResult): EnvironmentResult[] {
        return emailResult.environments.filter(env => env.users.length > 0);
    }

    /**
     * Calculate total users found across all search results
     */
    getTotalUsersFound (searchResults: EmailSearchResult[]): number {
        return searchResults.reduce((total, result) => total + result.totalUsers, 0);
    }

    /**
     * Get total number of environments
     */
    getTotalEnvironments (): number {
        return environment.apiEnvs.length;
    }
}
